import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { usePost } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { tokenManager } from "../../utils/apiComponent/apiClient"
import { toastUtils, showSuccessToast } from "../../utils/toast"
import { CheckCircle, XCircle, Loader2 } from "lucide-react"
import { useTranslation } from "../../i18n"

export default function VerifyEmailPage() {
  const { tObj } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("token") || ""
  const { execute, loading } = usePost(endpoints.verifyEmail, { isPublicRoute: true })
  const [status, setStatus] = useState('loading') // 'loading', 'success', 'error'

  useEffect(() => {
    let redirectTimeout = null
    let errorTimeout = null
    
    const run = async () => {
      if (!token) {
        setStatus('error')
        return
      }
      try {
        const res = await execute({ token })
        
        // Vérifier que la réponse contient les tokens (comme login)
        if (res?.accessToken) {
          // Stocker les tokens (accessToken et refreshToken)
          tokenManager.setTokens(res.accessToken, res.refreshToken)
          
          // Stocker les données utilisateur si disponibles
          if (res.user) {
            localStorage.setItem('userData', JSON.stringify(res.user))
          }
          
          // Déclencher la mise à jour de l'authentification
          // L'événement authChange est utilisé par useAuth()
          window.dispatchEvent(new CustomEvent('authChange', { 
            detail: { isAuthenticated: true, user: res.user } 
          }))
          
          // L'événement auth:changed est aussi déclenché par tokenManager.setTokens()
          // mais on le redispatch pour être sûr que tous les composants sont mis à jour
          window.dispatchEvent(new CustomEvent('auth:changed', { 
            detail: { isAuthenticated: true, user: res.user } 
          }))
          
          setStatus('success')
          showSuccessToast({ message: res.message || tObj.verifyEmail.emailVerifiedSuccess() })
          
          // Redirection automatique vers la page d'accueil après 2 secondes
          // L'utilisateur peut toujours cliquer sur "Start Now" pour rediriger immédiatement
          redirectTimeout = setTimeout(() => {
            window.location.href = '/'
          }, 2000)
        } else {
          // Si pas de token dans la réponse, traiter comme une erreur
          setStatus('error')
          toastUtils.showErrorToast({ message: tObj.verifyEmail.emailVerificationError() })
          errorTimeout = setTimeout(() => {
            navigate('/login', { replace: true })
          }, 3000)
        }
      } catch (e) {
        setStatus('error')
        // Gérer les différents types d'erreurs
        const errorMessage = e?.response?.data?.message || e?.message || tObj.verifyEmail.emailVerificationError()
        toastUtils.showErrorToast({ message: errorMessage })
        
        // Redirection vers login après 3 secondes
        errorTimeout = setTimeout(() => {
          navigate('/login', { replace: true })
        }, 3000)
      }
    }
    run()
    
    // Nettoyer les timeouts si le composant est démonté
    return () => {
      if (redirectTimeout) clearTimeout(redirectTimeout)
      if (errorTimeout) clearTimeout(errorTimeout)
    }
  }, [token, navigate, execute, tObj])

  const handleStart = () => {
    // Redispatch l'événement pour forcer la mise à jour de la navbar
    const userData = localStorage.getItem('userData')
    const user = userData ? JSON.parse(userData) : null
    
    // Vérifier que les tokens sont bien présents
    const accessToken = tokenManager.getToken()
    if (accessToken) {
      window.dispatchEvent(new CustomEvent('authChange', { 
        detail: { isAuthenticated: true, user: user } 
      }))
      
      window.dispatchEvent(new CustomEvent('auth:changed', { 
        detail: { isAuthenticated: true, user: user } 
      }))
      
      // Rediriger vers la page d'accueil
      window.location.href = '/'
    } else {
      // Si pas de token, rediriger vers login
      navigate('/login', { replace: true })
    }
  }

  const handleRetry = () => {
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Effets de fond animés */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl animate-float delay-500"></div>
      </div>

      <div className="w-full max-w-md bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border-0 text-center relative z-10 hover:shadow-[0_0_50px_rgba(31,58,138,0.2)] transition-all duration-300">
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full">
              <Loader2 className="w-12 h-12 text-[#203a8a] animate-spin" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent mb-4 animate-gradient">
              {tObj.verifyEmail.verifying()}
            </h1>
            <p className="text-gray-600 mb-6">{tObj.verifyEmail.pleaseWait()}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-green-100 to-emerald-100 rounded-full">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent mb-4 animate-gradient">
              {tObj.verifyEmail.emailVerified()}
            </h1>
            <p className="text-gray-600 mb-6">{tObj.verifyEmail.emailVerifiedDesc()}</p>
            <button
              onClick={handleStart}
              className="w-full h-12 lg:h-14 bg-gradient-to-r from-[#203a8a] to-[#2563eb] hover:from-[#1a1f4a] hover:to-[#1e40af] text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              {tObj.verifyEmail.startNow()}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-red-100 to-rose-100 rounded-full">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent mb-4 animate-gradient">
              {tObj.verifyEmail.verificationError()}
            </h1>
            <p className="text-gray-600 mb-6">
              {!token 
                ? tObj.verifyEmail.noToken()
                : tObj.verifyEmail.verificationFailed()
              }
            </p>
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full h-12 lg:h-14 bg-gradient-to-r from-[#203a8a] to-[#2563eb] hover:from-[#1a1f4a] hover:to-[#1e40af] text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                {tObj.verifyEmail.backToLogin()}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full h-12 lg:h-14 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-semibold shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300"
              >
                {tObj.verifyEmail.retry()}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}


